/**
 * 출결 입력 창(설계 §5-3 · 요청서 AT1~AT3 · D20) — 일정을 미리 잡지 않고 끝난 수업의 결과만 입력한다.
 *   수업 결과(진행 · 휴강) · 수업한 강사(대강이면 바꿈) · 학생별 출석 · 지각 · 조퇴 · 결석과 메모 · 회차 메모.
 *   [+ 보강·추가 회차] 로 시간표에 없는 회차도 같은 창에서 입력.
 * 저장하면 강사료 기준 「실제」 시간에 반영된다(요청서 §8-4) — 휴강은 0, 출석·지각·조퇴는 인정, 결석은 0.
 * 입력은 매니저(모든 반)와 강사(본인이 오늘 담임인 반의 회차만) — 다른 반은 서버가 403(요청서 §10).
 */
import React, { useEffect, useState } from 'react';
import { AcademyButton } from '../controls';
import { useAuth } from '../../../context/AuthContext';
import { getClass, listClassSessions, saveExtraSession, saveSessionResult } from '../academyApi';
import { formatMonthDay, isoWeekday, todayKST, weekdayLabel } from '../periodDates';
import { SegTabs, Spinner, useAsync } from '../ui';
import { DateInput, Field, FormModal, Note, SelectInput, TextInput, TimeInput, compactInputBase, useTeachers } from './form';

const STATUS = [['PRESENT', '출석'], ['LATE', '지각'], ['EARLY_LEAVE', '조퇴'], ['ABSENT', '결석']];
const hours = (a, b) => (a && b ? (Number(b.slice(0, 2)) * 60 + Number(b.slice(3, 5)) - Number(a.slice(0, 2)) * 60 - Number(a.slice(3, 5))) / 60 : 0);

function Roster({ rows, value, onChange, disabled }) {
  if (rows.length === 0) return <p className="py-2 text-xs text-gray-500">그날 이 반에 편성된 학생이 없습니다.</p>;
  return (
    <div className="max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-xl border border-gray-200 px-2">
      {rows.map((r) => {
        const v = value[r.student_id] || { status_code: 'PRESENT', memo: '' };
        return (
          <div key={r.student_id} className="flex flex-wrap items-center gap-2 py-1.5">
            <span className="w-20 shrink-0 truncate text-xs font-medium text-gray-900">{r.student_name}</span>
            <SegTabs
              tabs={STATUS}
              value={v.status_code}
              onChange={(code) => onChange({ ...value, [r.student_id]: { ...v, status_code: code } })}
              ariaLabel={`${r.student_name} 출결`}
            />
            <input
              aria-label={`${r.student_name} 메모`}
              value={v.memo}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, [r.student_id]: { ...v, memo: e.target.value } })}
              placeholder={v.status_code === 'LATE' ? '예: 10분' : v.status_code === 'ABSENT' ? '예: 병결' : '메모'}
              className={`${compactInputBase} w-24 flex-1`}
            />
          </div>
        );
      })}
    </div>
  );
}

/** 처음 값 — 이미 입력한 출결이 있으면 그것, 없으면 출석(설계 §5-3 「기본값 출석」) */
const initialMarks = (rows) => Object.fromEntries(rows.map((r) => [r.student_id, { status_code: r.status_code || 'PRESENT', memo: r.memo || '' }]));

function AttendanceModal({ classId, className, date, startTime, extra: startExtra = false, onClose }) {
  const { getAccessToken } = useAuth();
  const teachers = useTeachers();
  const [extra, setExtra] = useState(startExtra || !date);
  // 정규 회차 — 그날 회차를 명단과 함께 불러온다
  const day = useAsync((signal) => (extra ? Promise.resolve([]) : listClassSessions(classId, { from: date, to: date, withRoster: true, signal }, getAccessToken)), `att-${classId}-${date}-${extra}`);
  const sessions = (day.data || []).filter((s) => s.kind === 'REGULAR');
  const [start, setStart] = useState(startTime || '');
  const session = sessions.find((s) => s.start_time === (start || sessions[0]?.start_time));
  const [status, setStatus] = useState('HELD');
  const [taughtBy, setTaughtBy] = useState('');
  const [memo, setMemo] = useState('');
  const [marks, setMarks] = useState({});
  // 보강·추가 회차 — 날짜·시각을 받고, 그날 반 학생(C2)을 명단으로
  const [xDate, setXDate] = useState(date || todayKST());
  const [xStart, setXStart] = useState('');
  const [xEnd, setXEnd] = useState('');
  const [xKind, setXKind] = useState('MAKEUP');
  const members = useAsync((signal) => (extra && xDate ? getClass(classId, { asOf: xDate, signal }, getAccessToken) : Promise.resolve(null)), `att-members-${classId}-${xDate}-${extra}`);
  const extraRows = (members.data?.members || []).filter((m) => m.state !== 'paused').map((m) => ({ student_id: m.student_id, student_name: m.student_name, status_code: null, memo: '' }));

  useEffect(() => {
    if (!session) return;
    setStatus(session.status === 'canceled' ? 'CANCELED' : 'HELD');
    setTaughtBy(session.taught_by?.staff_id ? String(session.taught_by.staff_id) : '');
    setMemo(session.memo || '');
    setMarks(initialMarks(session.roster));
  }, [session?.date, session?.start_time, session?.result_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (extra) setMarks(initialMarks(extraRows)); }, [members.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = extra ? extraRows : (session?.roster || []);
  const attendance = rows.map((r) => ({ student_id: r.student_id, ...(marks[r.student_id] || { status_code: 'PRESENT', memo: '' }) }));
  const teacherName = teachers.find((t) => String(t.staff_id) === String(taughtBy))?.name || session?.taught_by?.name || '담임';
  const len = extra ? hours(xStart, xEnd) : hours(session?.start_time, session?.end_time);
  const title = extra ? `보강·추가 회차 — ${className}` : `출결 입력 — ${className} · ${date ? `${formatMonthDay(date)}(${weekdayLabel(isoWeekday(date))})` : ''} ${session ? `${session.start_time}~${session.end_time}` : ''}`;

  return (
    <FormModal
      onClose={onClose}
      title={title}
      size="lg"
      fixedHeight
      submitLabel="저장"
      canSubmit={extra ? !!xDate && !!xStart && !!xEnd && xEnd > xStart && !!taughtBy : !!session}
      onSubmit={() => (extra
        ? saveExtraSession(classId, { date: xDate, startTime: xStart, endTime: xEnd, kind: xKind, taughtByStaffId: Number(taughtBy), memo, attendance }, getAccessToken)
        : saveSessionResult(classId, session.date, session.start_time, {
          status, taughtByStaffId: taughtBy ? Number(taughtBy) : null, memo, attendance: status === 'CANCELED' ? [] : attendance,
        }, getAccessToken))}
    >
      {!extra && day.loading && !day.data && <Spinner label="회차 불러오는 중" />}
      {!extra && day.data && sessions.length === 0 && <Note tone="warn">그날 시간표에 이 반 수업이 없습니다. 보강·추가 회차로 입력해 주세요.</Note>}
      {!extra && sessions.length > 1 && (
        <Field label="회차"><SelectInput value={start || sessions[0].start_time} onChange={setStart} options={sessions.map((s) => [s.start_time, `${s.start_time}~${s.end_time}`])} /></Field>
      )}
      {extra && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Field label="날짜" required><DateInput value={xDate} onChange={setXDate} max={todayKST()} /></Field>
          <Field label="종류"><SelectInput value={xKind} onChange={setXKind} options={[['MAKEUP', '보강'], ['EXTRA', '추가 수업']]} /></Field>
          <Field label="시작 시각" required><TimeInput value={xStart} onChange={setXStart} /></Field>
          <Field label="끝 시각" required><TimeInput value={xEnd} onChange={setXEnd} /></Field>
        </div>
      )}
      {(extra || session) && (
        <>
          {!extra && (
            <Field label="수업 결과">
              <div className="flex"><SegTabs tabs={[['HELD', '진행'], ['CANCELED', '휴강']]} value={status} onChange={setStatus} ariaLabel="수업 결과" /></div>
            </Field>
          )}
          <Field label="수업한 강사" required={extra}>
            <SelectInput value={taughtBy} onChange={setTaughtBy} options={teachers.map((t) => [t.staff_id, t.name])} placeholder={extra ? '강사 선택' : '그날 담임'} />
          </Field>
          {status !== 'CANCELED' || extra ? (
            <Field label={`학생 ${rows.length}명`}>
              {extra && members.loading && !members.data ? <Spinner label="명단 불러오는 중" /> : <Roster rows={rows} value={marks} onChange={setMarks} />}
            </Field>
          ) : (
            <Note>휴강 · 0시간</Note>
          )}
          <Field label="회차 메모"><TextInput value={memo} onChange={setMemo} /></Field>
          {(status !== 'CANCELED' || extra) && <Note>{teacherName} · {len}시간</Note>}
          {!extra && (
            <AcademyButton type="button" onClick={() => setExtra(true)} variant="secondary">보강·추가 회차로 입력</AcademyButton>
          )}
        </>
      )}
    </FormModal>
  );
}

export default AttendanceModal;
